import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser, UserRole } from '@proref/types';
import type { LoginInput, SignupInput } from '@proref/schemas';
import * as authService from '../services/auth.service';
import { clearAppCache, ensureCacheOwner } from '../lib/query-client';

interface AuthContextValue {
  user: AuthUser | null;
  role: UserRole | null;
  // True only during the initial session check, so guards can wait instead of flashing /login.
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  logout: () => Promise<void>;
  // Replace the cached profile after a self-edit. Takes the user rather than refetching, since
  // PATCH /v1/me already returns the full /me shape — a second GET would only re-fetch what we
  // are holding.
  setProfile: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // On boot, resolve any persisted token into a user profile before rendering guards.
    async function bootstrap() {
      if (!authService.hasSession()) {
        if (active) setIsLoading(false);
        return;
      }
      try {
        const me = await authService.getMe();
        // Tag/verify cache ownership before rendering guards — if the persisted cache (already
        // rehydrated by the provider) belonged to another account, this wipes it now.
        ensureCacheOwner(me.id);
        if (active) setUser(me);
      } catch {
        // Invalid/expired session (refresh already cleared it via the api interceptor).
        if (active) setUser(null);
      } finally {
        if (active) setIsLoading(false);
      }
    }

    void bootstrap();

    // The api client fires this when a refresh fails — drop the user so guards redirect to /login,
    // and wipe the persisted cache so a dead session leaves no tenant data behind on the device.
    const onLogout = () => {
      clearAppCache();
      if (active) setUser(null);
    };
    window.addEventListener('auth:logout', onLogout);

    return () => {
      active = false;
      window.removeEventListener('auth:logout', onLogout);
    };
  }, []);

  async function login(input: LoginInput) {
    await authService.login(input);
    const me = await authService.getMe();
    // Clears the persisted cache if we're signing in as a different user than last time.
    ensureCacheOwner(me.id);
    setUser(me);
  }

  async function signup(input: SignupInput) {
    await authService.signup(input);
    const me = await authService.getMe();
    ensureCacheOwner(me.id);
    setUser(me);
  }

  async function logout() {
    await authService.logout();
    // Drop every cached query (in-memory + localStorage) so the next account starts clean.
    clearAppCache();
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, role: user?.role ?? null, isLoading, login, signup, logout, setProfile: setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
